import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { HiPlus, HiPencil, HiArrowLeft } from "react-icons/hi";
import { BsToggleOn, BsToggleOff } from "react-icons/bs";

import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";

import {
  SetProductFeatureEditAtom,
  GetProductCatFeatureAtom,
  SetProductCatFeatureAtom,
} from "../../store/productCategoryFeatureAtom";

import { settingsService } from "../../services/settingsService";

import ProductFeatureScroll from "./ProductFeatureScroll";
import ProductFeatureContent from "./ProductFeatureContent";

export default function ProductFeatureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);

  const getFeatureList = GetProductCatFeatureAtom();
  const setFeatureList = SetProductCatFeatureAtom();
  const setEditData = SetProductFeatureEditAtom();

  const [featureDetail, setFeatureDetail] = useState({});
  const [loading, setLoading] = useState(false);

  const myPromiseModal = createModal(GeneralModal);

  const [currModuleId, currModuleName, currUserMenu] = pathname.split("/").slice(1, 4);

  useEffect(() => {
    document.title = "Product Feature | Details";
    if (id) loadDetails(id);
  }, [id]);

  const loadDetails = async (featureId) => {
    setLoading(true);
    try {
      const res = await settingsService.productFeatureDetails(featureId);
      if (res?.data?.length) {
        const detail = res.data[0];
        setFeatureDetail(detail);

        // Update global list (atom)
        const updatedList = getFeatureList.product_feature_info.map((item) =>
          item.Product_Id === detail.Product_Id ? detail : item
        );
        setFeatureList((prev) => ({
          ...prev,
          product_feature_info: updatedList,
        }));
      }
    } catch (err) {
      logicContext.showToast("Failed to load feature details", "error");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => navigate(-1);

  const goToNewFeature = () => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  };

  const confirmAndEdit = async () => {
    const confirmed = await myPromiseModal({
      title: "Edit Feature",
      body: "Would you like to modify this product feature?",
      showInput: false,
    });

    if (confirmed?.decision) {
      setEditData(featureDetail);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  };

  const toggleStatus = async (shouldActivate) => {
    const status_id = shouldActivate ? 1 : 2;

    try {
      const res = await settingsService.productStatusChange({
        status_id,
        product_id: id,
      });

      const message = res.data?.[0]?.message_text || "Status updated";
      logicContext.showToast(message, "success");
      loadDetails(id);
    } catch (err) {
      logicContext.showToast("Failed to update status", "error");
    }
  };

  const isActive = featureDetail?.Status_Id === 1;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                title="Go back"
              >
                <HiArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Product Feature
              </h1>
              {featureDetail?.Product_Id && (
                <span className="text-sm text-gray-500 font-mono">
                  #{featureDetail.Product_Id}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Status Toggle */}
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-gray-200 bg-gray-50">
                <span className={`text-sm font-medium ${isActive ? "text-emerald-700" : "text-rose-700"}`}>
                  {isActive ? "Active" : "Inactive"}
                </span>
                <button
                  onClick={() => toggleStatus(!isActive)}
                  className={`text-3xl transition-colors ${
                    isActive ? "text-emerald-600 hover:text-emerald-700" : "text-rose-600 hover:text-rose-700"
                  }`}
                >
                  {isActive ? <BsToggleOn /> : <BsToggleOff />}
                </button>
              </div>

              {/* Edit Button */}
              <button
                onClick={confirmAndEdit}
                className="p-2.5 rounded-lg hover:bg-gray-100 text-blue-600 hover:text-blue-700 transition-colors"
                title="Edit Feature"
              >
                <HiPencil size={20} />
              </button>

              {/* New Feature Button */}
              <button
                onClick={goToNewFeature}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                title="Create New Feature"
              >
                <HiPlus size={18} />
                New Feature
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Feature List */}
        <aside className="w-80 xl:w-96 flex-shrink-0 border-r bg-white overflow-hidden">
          <ProductFeatureScroll />
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="max-w-[1400px] mx-auto p-6 lg:p-8">
              <ProductFeatureContent featureDetail={featureDetail} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}